// Tax form builders + PAYE calculations.
//
// Original BURS (Botswana) implementation is preserved as-is; the
// region-aware PAYE helpers below pull bracket tables from `region.ts`
// so the same logic serves South Africa and Nigeria. PDF generation
// happens in Edge Functions; this file is pure logic + types.

import type { JournalEntry, JournalLine } from '@nako/types';
import { getRegion, type RegionCode } from './region';

export interface VAT002Return {
  period_start: string; // YYYY-MM-DD
  period_end: string;
  output_vat: number; // sales VAT collected
  input_vat: number; // purchases VAT paid
  net_payable: number; // output - input (negative => refund due)
  zero_rated_sales: number; // not implemented v1, always 0
  exempt_sales: number; // not implemented v1, always 0
}

export function buildVat002(args: {
  entries: JournalEntry[];
  lines: JournalLine[];
  vatOutputAccountId: string;
  vatInputAccountId: string;
  from: string;
  to: string;
}): VAT002Return {
  const fromD = new Date(args.from);
  const toD = new Date(args.to);
  const inRange = args.entries.filter((e) => {
    if (!e.is_posted) return false;
    const d = new Date(e.entry_date);
    return d >= fromD && d <= toD;
  });
  const ids = new Set(inRange.map((e) => e.id));
  const lines = args.lines.filter((l) => ids.has(l.entry_id));

  let output_vat = 0;
  let input_vat = 0;

  for (const l of lines) {
    if (l.account_id === args.vatOutputAccountId) {
      output_vat += l.credit - l.debit; // output VAT is a liability (Cr balance)
    }
    if (l.account_id === args.vatInputAccountId) {
      input_vat += l.debit - l.credit; // input VAT is an asset (Dr balance)
    }
  }

  return {
    period_start: args.from,
    period_end: args.to,
    output_vat: round2(output_vat),
    input_vat: round2(input_vat),
    net_payable: round2(output_vat - input_vat),
    zero_rated_sales: 0,
    exempt_sales: 0,
  };
}

// ============================================================
// PAYE — region-aware
// ============================================================

interface PayeBracket {
  threshold: number;
  rate: number;
  cumulative: number;
}

const PAYE_BRACKETS_2026: PayeBracket[] = [
  { threshold: 48_000, rate: 0.0, cumulative: 0 },
  { threshold: 84_000, rate: 0.05, cumulative: 0 },
  { threshold: 120_000, rate: 0.125, cumulative: 1_800 },
  { threshold: 156_000, rate: 0.1875, cumulative: 6_300 },
  { threshold: Infinity, rate: 0.25, cumulative: 13_050 },
];

/** Annual PAYE on the BW 2026 brackets. */
export function annualPaye(annual: number): number {
  return annualPayeFromBrackets(annual, PAYE_BRACKETS_2026);
}

/** Monthly PAYE on the BW 2026 brackets. Annualises, taxes, then divides. */
export function monthlyPaye(monthlyGross: number): number {
  return round2(annualPaye(monthlyGross * 12) / 12);
}

/** Annual PAYE in the given region (BW / ZA / NG). Uses region.ts brackets. */
export function annualPayeFor(annual: number, region: RegionCode | string): number {
  const brackets = getRegion(region).paye;
  return annualPayeFromBrackets(annual, brackets);
}

/** Monthly PAYE in the given region. */
export function monthlyPayeFor(monthlyGross: number, region: RegionCode | string): number {
  return round2(annualPayeFor(monthlyGross * 12, region) / 12);
}

function annualPayeFromBrackets(annual: number, brackets: PayeBracket[]): number {
  if (annual <= 0) return 0;
  const bracket = brackets.find((b) => annual <= b.threshold);
  if (!bracket) return 0;
  const idx = brackets.indexOf(bracket);
  const prevThreshold = idx === 0 ? 0 : brackets[idx - 1]!.threshold;
  return round2(bracket.cumulative + (annual - prevThreshold) * bracket.rate);
}

// ============================================================
// Payroll levies (ZA UIF + SDL; BW and NG have none currently)
// ============================================================

export interface LevyResult {
  name: string;
  employee: number;
  employer: number;
}

/** Compute payroll levies on a monthly gross salary for the given region. */
export function monthlyLeviesFor(monthlyGross: number, region: RegionCode | string): LevyResult[] {
  const cfg = getRegion(region);
  return cfg.payrollLevies.map((l) => {
    const base = l.monthlyCeiling != null ? Math.min(monthlyGross, l.monthlyCeiling) : monthlyGross;
    return {
      name: l.name,
      employee: round2(base * l.employeeShare),
      employer: round2(base * l.employerShare),
    };
  });
}

// ============================================================
// PAYE schedule output (back-compat: BW)
// ============================================================

export interface PayeRow {
  employee_name: string;
  national_id: string;
  monthly_gross: number;
  paye: number;
  net_pay: number;
}

export function buildPayeSchedule(
  employees: Array<{
    name: string;
    national_id: string;
    monthly_gross: number;
  }>,
): PayeRow[] {
  return buildPayeScheduleFor(employees, 'BW');
}

/** Region-aware payroll schedule: PAYE + employee-side payroll levies. */
export function buildPayeScheduleFor(
  employees: Array<{
    name: string;
    national_id: string;
    monthly_gross: number;
  }>,
  region: RegionCode | string,
): PayeRow[] {
  return employees.map((e) => {
    const paye = monthlyPayeFor(e.monthly_gross, region);
    const levies = monthlyLeviesFor(e.monthly_gross, region);
    const employeeLevies = levies.reduce((sum, l) => sum + l.employee, 0);
    return {
      employee_name: e.name,
      national_id: e.national_id,
      monthly_gross: round2(e.monthly_gross),
      paye,
      net_pay: round2(e.monthly_gross - paye - employeeLevies),
    };
  });
}

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}
